import { lazy, Suspense, useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from '@/components/layout/Shell';
import { SettingsLayout } from '@/pages/settings/SettingsLayout';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireRole } from '@/components/RequireRole';
import { LogoLoading } from '@/components/ui/LogoLoading';
import { Dashboard } from '@/pages/Dashboard';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { SignupEmail } from '@/pages/SignupEmail';
import { Onboarding } from '@/pages/Onboarding';
import { NotFound } from '@/pages/NotFound';

// Wave-2 modules: Customers, Vendors, Chart of Accounts, Bank Accounts, Journal.
const AdminPage = lazyChunk(() => import('@/pages/Admin').then((m) => ({ default: m.Admin })));
const InvitePage = lazyChunk(() => import('@/pages/Invite').then((m) => ({ default: m.Invite })));
const CustomersRoutes = lazyChunk(() =>
  import('@/pages/customers').then((m) => ({ default: m.CustomersRoutes })),
);
const VendorsRoutes = lazyChunk(() =>
  import('@/pages/vendors').then((m) => ({ default: m.VendorsRoutes })),
);
const ChartOfAccounts = lazyChunk(() =>
  import('@/pages/accounting/ChartOfAccounts').then((m) => ({ default: m.ChartOfAccounts })),
);
const BankAccounts = lazyChunk(() =>
  import('@/pages/accounting/BankAccounts').then((m) => ({ default: m.BankAccounts })),
);
const JournalViewer = lazyChunk(() =>
  import('@/pages/accounting/JournalViewer').then((m) => ({ default: m.JournalViewer })),
);


// Wave-3 Group A: Invoices, Quotations, Bills, Expenses.
const Invoices = lazyChunk(() =>
  import('@/pages/invoices').then((m) => ({ default: m.InvoicesRoutes })),
);
const Quotations = lazyChunk(() =>
  import('@/pages/quotations').then((m) => ({ default: m.QuotationsRoutes })),
);
const Bills = lazyChunk(() =>
  import('@/pages/bills').then((m) => ({ default: m.BillsRoutes })),
);
const Expenses = lazyChunk(() =>
  import('@/pages/expenses').then((m) => ({ default: m.ExpensesRoutes })),
);
const Payments = lazyChunk(() =>
  import('@/pages/payments').then((m) => ({ default: m.PaymentsRoutes })),
);
/* The catalogue is NOT gated on the inventory module and the stock screens
   are — see the Sidebar. The route itself stays open either way: the server is
   the gate, and a 403 inside the page reads better than a 404 on the URL
   somebody was sent. */
const Items = lazyChunk(() => import('@/pages/items').then((m) => ({ default: m.ItemsRoutes })));
const Inventory = lazyChunk(() =>
  import('@/pages/inventory').then((m) => ({ default: m.InventoryRoutes })),
);
const Employees = lazyChunk(() =>
  import('@/pages/employees').then((m) => ({ default: m.EmployeesRoutes })),
);
const Payroll = lazyChunk(() =>
  import('@/pages/payroll').then((m) => ({ default: m.PayrollRoutes })),
);
const Subscriptions = lazyChunk(() =>
  import('@/pages/subscriptions').then((m) => ({ default: m.SubscriptionsRoutes })),
);
const Contracts = lazyChunk(() =>
  import('@/pages/contracts').then((m) => ({ default: m.ContractsRoutes })),
);
const Reports = lazyChunk(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsRoutes })),
);
const Settings = lazyChunk(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsRoutes })),
);
const Users = lazyChunk(() =>
  import('@/pages/users').then((m) => ({ default: m.UsersRoutes })),
);
const Billing = lazyChunk(() =>
  import('@/pages/settings/Billing').then((m) => ({ default: m.Billing })),
);
const Account = lazyChunk(() => import('@/pages/Account').then((m) => ({ default: m.Account })));
const MemoryPage = lazyChunk(() => import('@/pages/Memory').then((m) => ({ default: m.MemoryPage })));

function Loading(): ReactNode {
  return (
    <div className="flex min-h-60 items-center justify-center">
      <LogoLoading size={72} />
    </div>
  );
}

/**
 * A LAZY IMPORT THAT SURVIVES A DEPLOY.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BLACK SCREEN
 *
 * Every route below is a separate hashed chunk. When a release ships, the
 * hashes change and the old files stop existing. A browser that has had this
 * app open across a deploy is still running the OLD index.html, so the moment
 * somebody navigates to a page they have not visited yet it asks for a chunk
 * that is no longer there, the import rejects, Suspense has nothing to render,
 * and the screen goes black. Pressing refresh fetches the new index.html and
 * everything works, which is exactly why this reads as "the page is broken
 * until I reload" rather than as a deploy.
 *
 * It is invisible in development, where nothing is hashed, and invisible in
 * testing, where nobody has the app open during a release. It was reported by
 * the customer on a day with nine deploys.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DEFENCES, AND THIS IS THE SECOND
 *
 * The first is in infra/aws/deploy.sh: a release now inherits the previous
 * release's assets, so an old hash keeps resolving. That fixes the common case
 * without the customer noticing anything.
 *
 * This is what happens when that is not enough: three deploys later the release
 * holding their chunk has been pruned, or a proxy served a stale index. Rather
 * than a black screen, the page reloads itself once.
 *
 * ONCE is the whole design. A reload loop on a genuinely missing file is worse
 * than a blank page: it is a blank page that also burns battery and cannot be
 * read. The flag lives in sessionStorage so a second failure in the same tab
 * falls through to the error, and a new tab gets one fresh attempt.
 */
const RELOAD_FLAG = 'railit_chunk_reloaded';

function lazyChunk<T extends ComponentType<unknown>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((err: unknown) => {
      let already = 'yes';
      try {
        already = sessionStorage.getItem(RELOAD_FLAG) ?? '';
        sessionStorage.setItem(RELOAD_FLAG, 'yes');
      } catch {
        /* Private mode, or storage disabled. Fall through to the error rather
           than reloading blind, which is the case that loops. */
      }
      if (!already) {
        window.location.reload();
        /* Never resolves: the reload is already in flight and rendering an
           error for a page that is about to be replaced is a flash of noise. */
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}

/* Cleared on a successful load, so the next deploy gets its own attempt rather
   than inheriting a flag set weeks ago. */
function clearReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

function Lazy({ Component }: { Component: ComponentType }): ReactNode {
  useEffect(clearReloadFlag, []);
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/email" element={<SignupEmail />} />
      {/* Outside RequireAuth on purpose: an account here holds an onboarding
          token, not a session, and has no organization to be authorised for. */}
      <Route path="/onboarding" element={<Onboarding />} />
      {/* Public for the same reason: the token IS the credential, and the
          person holding it has no account yet in the common case. */}
      <Route path="/invite/:token" element={<Lazy Component={InvitePage} />} />

      <Route element={<RequireAuth />}>
        <Route element={<Shell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />

          <Route path="/customers/*" element={<Lazy Component={CustomersRoutes} />} />
          <Route path="/vendors/*" element={<Lazy Component={VendorsRoutes} />} />

          <Route
            path="/accounting/chart-of-accounts"
            element={<Lazy Component={ChartOfAccounts} />}
          />
          <Route
            path="/accounting/bank-accounts"
            element={<Lazy Component={BankAccounts} />}
          />
          <Route path="/accounting/journal" element={<Lazy Component={JournalViewer} />} />

          <Route path="/invoices/*" element={<Lazy Component={Invoices} />} />
          <Route path="/quotations/*" element={<Lazy Component={Quotations} />} />
          <Route path="/bills/*" element={<Lazy Component={Bills} />} />
          <Route path="/expenses/*" element={<Lazy Component={Expenses} />} />
          <Route path="/payments/*" element={<Lazy Component={Payments} />} />
          <Route path="/items/*" element={<Lazy Component={Items} />} />
          <Route path="/inventory/*" element={<Lazy Component={Inventory} />} />
          <Route path="/employees/*" element={<Lazy Component={Employees} />} />
          <Route path="/payroll/*" element={<Lazy Component={Payroll} />} />
          <Route path="/subscriptions/*" element={<Lazy Component={Subscriptions} />} />
          <Route path="/contracts/*" element={<Lazy Component={Contracts} />} />
          <Route path="/reports/*" element={<Lazy Component={Reports} />} />
          {/*
            Billing is its OWN page, deliberately outside the settings rail.
            It is not configuration — it is the subscription, three priced
            tiers, the AI meter and the add-on catalogue, and none of that can
            be compared inside the ~640px column the rail leaves behind. It
            keeps the app shell (sidebar, topbar); it just does not inherit
            Settings' second navigation.
          */}
          <Route
            path="/billing"
            element={
              <RequireRole allow={['Owner', 'Admin']}>
                <Lazy Component={Billing} />
              </RequireRole>
            }
          />
          {/* Everything already links to the old path. Keep it working. */}
          <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />

          {/*
            Your account, not your company. Deliberately NOT under RequireRole:
            a Viewer in one company is still the owner of their own identity,
            and this is the only page that reaches every company they belong to.
          */}
          <Route path="/account" element={<Lazy Component={Account} />} />
          {/* What the agent remembers. Not under RequireRole: everyone can see
              their own memories and the company facts that shape their answers,
              and only writing a COMPANY one is gated — see routes/erp/memory.ts. */}
          <Route path="/memory" element={<Lazy Component={MemoryPage} />} />

          {/* Settings and Users share one shell: the rail is a layout, so
              jumping to People does not throw you out of configuration. */}
          <Route element={<SettingsLayout />}>
            <Route
              path="/settings/*"
              element={
                <RequireRole allow={['Owner', 'Admin']}>
                  <Lazy Component={Settings} />
                </RequireRole>
              }
            />
            <Route path="/users/*" element={<Lazy Component={Users} />} />
          </Route>

          {/* The staff console. Guarded twice: the API refuses anyone without
              is_platform_admin, and the page itself declines to render for a
              customer who typed the URL. */}
          <Route path="/admin" element={<Lazy Component={AdminPage} />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
